import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import EventIcon from "@mui/icons-material/Event";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import QuizIcon from "@mui/icons-material/Quiz";
import PlaceIcon from "@mui/icons-material/Place";
import {
  fetchClassById,
  fetchSessionsForCourse,
  fetchStudentAttendance,
  fetchChecklistsForCourse,
  fetchChecklistEvaluation,
  fetchTestsForCourse,
  fetchLatestTestSubmission,
} from "../api/backend";
import { useTenant } from "../contexts/TenantContext";
import { useTheme, alpha } from "@mui/material/styles";

const DS = {
  red: "var(--color-danger)",
  redLight: "var(--color-danger-bg)",
  grey: "var(--color-ink-3)",
  greyBorder: "#E5E7EB",
  surface: "var(--color-surface-2)",
  text: "#111827",
  textSecondary: "var(--color-ink-3)",
  cardBg: "#ffffff",
};

function SectionHeader({ icon, label, count, total }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tSecondary = theme.palette.secondary.main;
  const tSecondaryLight = alpha(tSecondary, 0.08);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box sx={{ color: tPrimary, display: "flex" }}>{icon}</Box>
      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: DS.text, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-display)" }}>
        {label}
      </Typography>
      {count != null && total != null && (
        <Chip
          label={`${count}/${total}`}
          size="small"
          sx={{
            ml: "auto", height: 20, fontSize: "0.72rem", fontWeight: 700,
            bgcolor: count === total ? tSecondaryLight : DS.surface,
            color: count === total ? tSecondary : DS.textSecondary,
          }}
        />
      )}
    </Box>
  );
}

function StatusPill({ passed, label }) {
  const theme = useTheme();
  const tSecondary = theme.palette.secondary.main;
  const tSecondaryLight = alpha(tSecondary, 0.08);
  const tAccent = theme.palette.warning.main;
  const tAccentLight = alpha(tAccent, 0.08);
  const color = passed === true ? tSecondary : passed === false ? DS.red : tAccent;
  const bg = passed === true ? tSecondaryLight : passed === false ? DS.redLight : tAccentLight;
  const text = label || (passed === true ? "Passed" : passed === false ? "Failed" : "Pending");
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      px: 1, py: 0.25, borderRadius: "6px",
      bgcolor: bg, color, fontWeight: 700, fontSize: "0.7rem",
    }}>
      {text}
    </Box>
  );
}

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function ClassArchivePage() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useTenant();
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tSecondary = theme.palette.secondary.main;
  const tSecondaryLight = alpha(tSecondary, 0.08);

  const [cls, setCls] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [checklists, setChecklists] = useState([]);
  const [evaluations, setEvaluations] = useState({});
  const [tests, setTests] = useState([]);
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !classId) return;
    const id = Number(classId);

    async function load() {
      const [classData, sessionsData, attendanceData, checklistsData, testsData] = await Promise.all([
        fetchClassById(id).catch(() => null),
        fetchSessionsForCourse(id).catch(() => []),
        fetchStudentAttendance(id, user.id).catch(() => []),
        fetchChecklistsForCourse(id).catch(() => []),
        fetchTestsForCourse(id).catch(() => []),
      ]);

      setCls(classData);
      setSessions(Array.isArray(sessionsData) ? sessionsData : []);

      const attMap = {};
      for (const a of (Array.isArray(attendanceData) ? attendanceData : [])) {
        attMap[a.sessionId] = a;
      }
      setAttendanceMap(attMap);

      const safeChecklists = Array.isArray(checklistsData) ? checklistsData : [];
      setChecklists(safeChecklists);

      const safeTests = Array.isArray(testsData) ? testsData : [];
      setTests(safeTests);

      const [evalResults, testResultsList] = await Promise.all([
        Promise.all(
          safeChecklists.map((c) =>
            fetchChecklistEvaluation(id, c.id, user.id)
              .then((ev) => ({ id: c.id, ev }))
              .catch(() => ({ id: c.id, ev: null }))
          )
        ),
        Promise.all(
          safeTests.map((t) =>
            fetchLatestTestSubmission(t.id, user.id)
              .then((res) => ({ id: t.id, res }))
              .catch(() => ({ id: t.id, res: null }))
          )
        ),
      ]);

      const evMap = {};
      for (const { id: cid, ev } of evalResults) evMap[cid] = ev;
      setEvaluations(evMap);

      const trMap = {};
      for (const { id: tid, res } of testResultsList) trMap[tid] = res;
      setTestResults(trMap);

      setLoading(false);
    }

    load();
  }, [user, classId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const attendedCount = sessions.filter((s) => attendanceMap[s.id]?.attended).length;
  const checklistsPassed = checklists.filter((c) => evaluations[c.id]?.passed === true).length;
  const testsPassed = tests.filter((t) => testResults[t.id]?.submission?.passed === true).length;

  return (
    <Box sx={{ maxWidth: 820, mx: "auto", px: { xs: 2, sm: 3 }, pt: 3, pb: 8 }}>
      {/* Back */}
      <Box
        onClick={() => navigate("/transcript")}
        sx={{
          display: "inline-flex", alignItems: "center", gap: 0.75,
          fontSize: "0.8rem", fontWeight: 600, color: DS.textSecondary,
          cursor: "pointer", mb: 2.5,
          "&:hover": { color: tPrimary },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} />
        My Classes
      </Box>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: DS.textSecondary, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>
          {cls?.category || "Class Record"}
        </Typography>
        <Typography
          variant="h5"
          fontWeight={800}
          color={tPrimary}
          sx={{
            letterSpacing: "-0.02em",
            mb: 0.5,
            fontFamily: "var(--font-display)",
          }}
        >
          {cls?.title || "—"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          {cls?.courseNumber && (
            <Typography sx={{ fontSize: "0.78rem", fontFamily: "'JetBrains Mono', monospace", color: DS.textSecondary }}>
              {cls.courseNumber}
            </Typography>
          )}
          <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: DS.greyBorder }} />
          <Typography sx={{ fontSize: "0.78rem", color: DS.textSecondary }}>
            Completed record
          </Typography>
        </Box>
      </Box>

      <Stack spacing={2.5}>
        {/* Sessions */}
        {sessions.length > 0 && (
          <Paper elevation={0} sx={{ border: `1px solid ${DS.greyBorder}`, borderRadius: "12px", p: 2.5 }}>
            <SectionHeader
              icon={<EventIcon sx={{ fontSize: 18 }} />}
              label="Sessions"
              count={attendedCount}
              total={sessions.length}
            />
            <Stack spacing={0} divider={<Divider />}>
              {sessions.map((s) => {
                const att = attendanceMap[s.id];
                const attended = att?.attended === true;
                const noRecord = !att;
                const dateStr = formatDate(s.sessionDate || s.startTime);
                return (
                  <Box
                    key={s.id}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25 }}
                  >
                    {attended ? (
                      <CheckCircleIcon sx={{ fontSize: 18, color: tSecondary, flexShrink: 0 }} />
                    ) : noRecord ? (
                      <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: DS.greyBorder, flexShrink: 0 }} />
                    ) : (
                      <CancelIcon sx={{ fontSize: 18, color: DS.red, flexShrink: 0 }} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: DS.text }}>
                        {s.title || `Session ${sessions.indexOf(s) + 1}`}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.25 }}>
                        {dateStr && (
                          <Typography sx={{ fontSize: "0.72rem", color: DS.textSecondary }}>{dateStr}</Typography>
                        )}
                        {s.location && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                            <PlaceIcon sx={{ fontSize: 11, color: DS.textSecondary }} />
                            <Typography sx={{ fontSize: "0.72rem", color: DS.textSecondary }}>{s.location}</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <StatusPill
                      passed={noRecord ? null : attended}
                      label={noRecord ? "No record" : attended ? "Attended" : "Absent"}
                    />
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        )}

        {/* Skill Sheets */}
        {checklists.length > 0 && (
          <Paper elevation={0} sx={{ border: `1px solid ${DS.greyBorder}`, borderRadius: "12px", p: 2.5 }}>
            <SectionHeader
              icon={<AssignmentTurnedInIcon sx={{ fontSize: 18 }} />}
              label="Skill Sheets"
              count={checklistsPassed}
              total={checklists.length}
            />
            <Stack spacing={1.5}>
              {checklists.map((c) => {
                const ev = evaluations[c.id];
                const passed = ev?.passed;
                const signedOff = ev?.signedOffAt || ev?.signedOff;
                const items = Array.isArray(ev?.items) ? ev.items : [];
                const completedItems = items.filter((i) => i.status === "PASS" || i.status === "pass" || i.completed).length;

                return (
                  <Box key={c.id} sx={{ border: `1px solid ${DS.greyBorder}`, borderRadius: "10px", p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: items.length > 0 ? 1.25 : 0 }}>
                      <Box>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: DS.text }}>
                          {c.title || c.name || `Checklist #${c.id}`}
                        </Typography>
                        {signedOff && (
                          <Typography sx={{ fontSize: "0.7rem", color: DS.textSecondary, mt: 0.25 }}>
                            Signed off {formatDate(signedOff)}
                          </Typography>
                        )}
                        {items.length > 0 && (
                          <Typography sx={{ fontSize: "0.7rem", color: DS.textSecondary, mt: 0.25 }}>
                            {completedItems}/{items.length} items
                          </Typography>
                        )}
                      </Box>
                      <StatusPill passed={ev ? passed : null} label={!ev ? "Pending" : undefined} />
                    </Box>

                    {items.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {items.map((item, idx) => {
                          const ok = item.status === "PASS" || item.status === "pass" || item.completed;
                          return (
                            <Box
                              key={item.id || idx}
                              sx={{
                                display: "flex", alignItems: "center", gap: 0.5,
                                px: 1, py: 0.4, borderRadius: "6px",
                                bgcolor: ok ? tSecondaryLight : DS.surface,
                                border: `1px solid ${ok ? "var(--color-success-bg)" : DS.greyBorder}`,
                              }}
                            >
                              {ok
                                ? <CheckCircleIcon sx={{ fontSize: 11, color: tSecondary }} />
                                : <RadioButtonUncheckedIcon sx={{ fontSize: 11, color: DS.grey }} />
                              }
                              <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: ok ? tSecondary : DS.grey }}>
                                {item.label || item.name || item.description || `Item ${idx + 1}`}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        )}

        {/* Written Tests */}
        {tests.length > 0 && (
          <Paper elevation={0} sx={{ border: `1px solid ${DS.greyBorder}`, borderRadius: "12px", p: 2.5 }}>
            <SectionHeader
              icon={<QuizIcon sx={{ fontSize: 18 }} />}
              label="Written Tests"
              count={testsPassed}
              total={tests.length}
            />
            <Stack spacing={0} divider={<Divider />}>
              {tests.map((t) => {
                const result = testResults[t.id];
                const sub = result?.submission;
                const pct = sub?.totalPoints > 0
                  ? Math.round((sub.score / sub.totalPoints) * 100)
                  : null;
                const passed = sub?.passed;
                const submittedAt = formatDate(sub?.submittedAt);

                return (
                  <Box key={t.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: DS.text }}>
                        {t.title || `Test #${t.id}`}
                      </Typography>
                      {submittedAt && (
                        <Typography sx={{ fontSize: "0.72rem", color: DS.textSecondary, mt: 0.25 }}>
                          Submitted {submittedAt}
                        </Typography>
                      )}
                    </Box>
                    {pct != null && (
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: passed ? tSecondary : DS.red }}>
                        {pct}%
                      </Typography>
                    )}
                    <StatusPill passed={sub ? passed : null} label={!sub ? "Not taken" : undefined} />
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        )}

        {sessions.length === 0 && checklists.length === 0 && tests.length === 0 && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography color={DS.textSecondary} fontSize="0.9rem">No records found for this class.</Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
